import React, { useState, useEffect, CSSProperties } from "react";
import { useLocation, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";

// External
import { parse } from "query-string";
import { GridRowParams, useGridApiRef } from "@mui/x-data-grid-pro";
import type {
  GridColumns,
  GridRowModel,
  GridSortModel,
  GridSortItem,
  DataGridProProps,
  GridRowClassNameParams,
} from "@mui/x-data-grid-pro";
import { Spin } from "antd";
import { css } from "@mui/material";
import { v4 as uuid } from "uuid";

// Hooks
import useWindowResize from "hooks/useWindowResize";
import { UseNavigation } from "hooks/useNavigation";

import { toSnakeCase, formatNumberWithCommas } from "lib/helpers";
import { actions } from "rdx";
import { OrgProjectsCsvT, TeamUsersCsvT } from "rdx/modules/csvReports/types";

// Components
import { NoResultsOverlay } from "components/Icons/NoResultsOverlay/NoResultsOverlay";
import SearchFilterCSV from "components/Pagination/SearchFilterPagination/SearchFilterCSV";
import {
  PlusOutlined,
  DownloadIcon,
  DownCaretFilled,
  UpCaretFilled,
  DownCaretThicker,
  RightCaretIcon,
} from "components/Icons";
import { cvar } from "styles";
import { FiltersT } from "components/Pagination/SearchFilterPagination/NewFilters/types";
import { theme } from "styles/themes";
import { PrimaryButton } from "components/mui-styled-components";

import * as S from "./styles";

type BasicTablePropsT<TData extends GridRowModel> = {
  rows: TData[];
  columns: GridColumns;
  sortItems?: FiltersT;
  getCsv?: (search: Record<string, unknown>) => void;
  getCsvReport?: ({ reportId }: { reportId: number }) => void;
  selectedCsv?: Record<string, number | string | null> | OrgProjectsCsvT | TeamUsersCsvT;
  navigate?: ReturnType<UseNavigation>["navigate"];
  override?: ReturnType<UseNavigation>["override"];
  totalResultCount?: number;
  isLoading?: boolean;
  getData?: (payload?: Record<string, unknown>) => void;
  resetData?: () => void;
  onRowClick?: (params: GridRowParams<TData>) => void;
  defaultSort?: GridSortItem;
  rightSideComponent?: React.ReactElement;
  isMobile?: boolean; // after all tables have the same design could be removed
  hideFilterBar?: boolean;
  hideColumnHeaders?: boolean;
  hideBottomPadding?: boolean;
  noXScroll?: boolean;
  containerStyles?: CSSProperties;
  hideCSV?: boolean;
  handleFilterBarButtonClick?: () => void;
  showFilterBarButton?: boolean;
  filterBarButtonText?: string;
  hideSearchInput?: boolean;
  rowHeight?: number;
  treeData?: boolean;
  columnLeftRightPadding?: `${number}px`;
  customSearch?: JSX.Element;
  isRefetchingTableData?: boolean;
  showDateFilterButton?: boolean;
  transparentMobileRows?: boolean;
  treeDataIndentation?: boolean;
  treeDataControlColWidth?: number;
  showTeamViewButton?: boolean;
} & DataGridProProps;

type TableLoadingOverlayPropsT = {
  overlayEntireTable?: boolean;
  isRefetching?: boolean;
};

const TableLoadingOverlay = ({ overlayEntireTable, isRefetching }: TableLoadingOverlayPropsT) => (
  <S.LoadingOverlay overlayEntireTable={overlayEntireTable}>
    <Spin size={isRefetching ? "large" : "default"} />
  </S.LoadingOverlay>
);

const ColumnUnsortedIcon = () => <DownCaretFilled fill={cvar("nepal-blue")} />;

const ColumnSortedAscendingIcon = () => <UpCaretFilled fill={cvar("blue-ribbon")} />;

const ColumnSortedDescendingIcon = () => <DownCaretFilled fill={cvar("blue-ribbon")} />;

const TreeDataCollapseIcon = () => <DownCaretThicker fill={theme.colors["darkest-blue"]} />;

const TreeDataExpandIcon = () => <RightCaretIcon fill={theme.colors["darkest-blue"]} />;

const BasicTable = <T extends Record<string, unknown>>({
  rows,
  columns,
  getCsv,
  getCsvReport,
  selectedCsv,
  sortItems,
  navigate,
  override,
  totalResultCount,
  isLoading,
  getData,
  resetData,
  onRowClick,
  defaultSort,
  rightSideComponent,
  isMobile,
  showFilterBarButton,
  handleFilterBarButtonClick,
  filterBarButtonText,
  getTreeDataPath,
  groupingColDef,
  columnLeftRightPadding,
  hideBottomPadding = false,
  hideFilterBar = false,
  hideColumnHeaders = false,
  containerStyles = {},
  noXScroll = false,
  hideCSV = false,
  rowHeight = 78,
  treeData = false,
  hideSearchInput = false,
  customSearch,
  isRefetchingTableData = false,
  showDateFilterButton = false,
  transparentMobileRows = false,
  treeDataIndentation = false,
  treeDataControlColWidth,
  showTeamViewButton = false,
}: BasicTablePropsT<T>) => {
  const dispatch = useDispatch();
  const { search } = useLocation();
  const params: { tab: string } = useParams();
  const { width } = useWindowResize();
  const apiRef = useGridApiRef();
  const tablet = width <= 768;

  const [csvPending, setCsvPending] = useState(false);
  const [csvIntervalId, setCsvIntervalId] = useState<number | null>(null);
  const [sortModel, setSortModel] = useState<GridSortModel>(defaultSort ? [defaultSort] : []);
  const showPrimaryBtn = showFilterBarButton && filterBarButtonText && handleFilterBarButtonClick;

  const csvText = () => {
    if (tablet) return "CSV";

    return csvPending ? "Preparing CSV" : "Export CSV";
  };

  const getReport = (interval: number, reportId: number) => {
    const intervalId = window.setInterval(() => {
      if (reportId && getCsvReport) getCsvReport({ reportId });
    }, interval);

    setCsvIntervalId(intervalId);
  };

  useEffect(() => {
    if (csvPending) {
      if (selectedCsv?.url) {
        setCsvPending(false);
        clearIntervalIds();
        window.open(selectedCsv.url as string, "_blank");
      } else if (selectedCsv?.jobStatus === "error") {
        setCsvPending(false);
        clearIntervalIds();
      } else if (selectedCsv?.id && !csvIntervalId) {
        getReport(2000, selectedCsv.id as number);
      }
    }
  }, [selectedCsv]); // eslint-disable-line react-hooks/exhaustive-deps

  const createCsv = () => {
    if (!rows.length) {
      dispatch(
        actions.setAlertMessageVisible({
          message: "No data available to export with the current filters.",
          severity: "warning",
          duration: 6000,
        }),
      );
    } else if (getCsv) {
      getCsv(parse(search));
      setCsvPending(true);
    }
  };

  const clearIntervalIds = () => {
    if (csvIntervalId) {
      clearInterval(csvIntervalId);
      setCsvIntervalId(null);
    }
  };

  const columnHeaderHeight = () => {
    const gapBetweenHeaderAndRows = 8;
    if (isMobile || hideColumnHeaders) {
      return gapBetweenHeaderAndRows;
    }
    return 38 + gapBetweenHeaderAndRows;
  };

  const assignRowClassName = (classParams: GridRowClassNameParams): string => {
    const { row } = classParams;
    if (row.hierarchy?.length && treeDataIndentation) {
      return `tree-data-indentation--${row.hierarchy.indexOf(row.id)}`;
    }

    return "basic-table-row";
  };

  return (
    <S.Container style={containerStyles}>
      {!hideFilterBar && navigate && (
        <S.SearchFilterContainer isMobile={isMobile}>
          <SearchFilterCSV
            noSearch={hideSearchInput}
            sortItems={sortItems}
            navigate={navigate}
            override={override}
            noCSV
            isLoading={isLoading}
            customSearch={customSearch}
            showDateFilterButton={showDateFilterButton}
            showTeamViewButton={showTeamViewButton}
          />
          <S.AlignCenter>
            {rightSideComponent}
            {!!totalResultCount && <S.Results>{formatNumberWithCommas(totalResultCount || 0)} Results</S.Results>}
            {!hideCSV && (
              <S.CSVButton onClick={createCsv} variant="contained" disabled={csvPending} isActive={csvPending}>
                {!tablet && <DownloadIcon fill={csvPending ? theme.colors.white : theme.colors["blue-ribbon"]} />}
                <span css={csvPending ? S.loadingCsv : S.csvText}>{csvText()}</span>
              </S.CSVButton>
            )}

            {showPrimaryBtn && (
              <PrimaryButton
                name="filter-bar-button"
                gradient
                css={css`
                  height: 28px;
                  gap: 8px;
                  margin-left: 9px;
                `}
                onClick={handleFilterBarButtonClick}
              >
                <S.StyledContentContainer>
                  <PlusOutlined
                    width="18"
                    height="18"
                    fill={`linear-gradient(270.54deg, ${cvar("picton-blue")} 49.88%, ${cvar("blue-ribbon")} 135.73%)`}
                    innerFill={cvar("white")}
                  />
                  {filterBarButtonText}
                </S.StyledContentContainer>
              </PrimaryButton>
            )}
          </S.AlignCenter>
        </S.SearchFilterContainer>
      )}

      <S.StyledDataGridPro
        isMobile={isMobile}
        transparentMobileRows={transparentMobileRows}
        hideColumnHeaders={hideColumnHeaders}
        hideBottomPadding={hideBottomPadding}
        noXScroll={noXScroll}
        columnLeftRightPadding={columnLeftRightPadding}
        onRowClick={(rowParams) => {
          if (onRowClick) {
            onRowClick(rowParams);
          }
        }}
        clickableRow={!!onRowClick}
        getRowId={(row) => row.projectId || row.id || uuid()}
        disableColumnMenu
        headerHeight={columnHeaderHeight()}
        rowHeight={isMobile ? 106 : rowHeight}
        apiRef={apiRef}
        loading={isLoading || isRefetchingTableData}
        sortingMode="server"
        sortModel={sortModel}
        onSortModelChange={(e) => {
          setSortModel(e);
          if (resetData) {
            resetData();
          }
          if (navigate) {
            const newSearch = { sort: e[0] ? `${toSnakeCase(e[0].field)}_${e[0].sort}` : null };
            navigate(newSearch);
          }
        }}
        addLoadingSpace={isLoading}
        onRowsScrollEnd={() => {
          const { top } = apiRef.current.getScrollPosition();
          if (getData && top !== 0) {
            getData({ ...parse(search), tab: toSnakeCase(params.tab) });
          }
        }}
        components={{
          LoadingOverlay: TableLoadingOverlay,
          NoRowsOverlay: NoResultsOverlay,
          NoResultsOverlay,
          ColumnUnsortedIcon,
          ColumnSortedAscendingIcon,
          ColumnSortedDescendingIcon,
          TreeDataCollapseIcon,
          TreeDataExpandIcon,
        }}
        componentsProps={{
          loadingOverlay: {
            overlayEntireTable: rows.length === 0 || isRefetchingTableData,
            isRefetching: isRefetchingTableData,
          },
        }}
        columns={columns}
        rows={rows}
        treeData={treeData}
        getTreeDataPath={getTreeDataPath}
        groupingColDef={groupingColDef}
        defaultGroupingExpansionDepth={-1}
        getRowClassName={(classNameParams) => assignRowClassName(classNameParams)}
        treeDataControlColWidth={treeDataControlColWidth}
      />
      {isMobile && showPrimaryBtn && (
        <S.MobileFixedFooter>
          <S.StyledMobPrimaryButton name="add-lead" gradient onClick={handleFilterBarButtonClick}>
            <S.StyledContentContainer>{filterBarButtonText}</S.StyledContentContainer>
          </S.StyledMobPrimaryButton>
        </S.MobileFixedFooter>
      )}
    </S.Container>
  );
};

export default BasicTable;
